import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  CheckoutElementsProvider,
  ExpressCheckoutElement,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { useTranslation } from 'react-i18next';
import './CheckoutPage.css';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.glowapp.me';
const STRIPE_LOCALES = ['en', 'he', 'ar', 'ru'];
const APP_PRIMARY = '#3949AB';
const LANDING_PRIMARY = '#7c5cbf';

function readClientSecret() {
  if (typeof window === 'undefined') return '';
  const hash = (window.location.hash || '').replace(/^#/, '').trim();
  if (hash.startsWith('cs_') && hash.includes('_secret_')) return hash;
  return '';
}

function stripeLocale(lang) {
  const primary = String(lang || 'en').split('-')[0].toLowerCase();
  return STRIPE_LOCALES.includes(primary) ? primary : 'en';
}

async function loadPublishableKey() {
  if (PUBLISHABLE_KEY) return PUBLISHABLE_KEY;
  if (!API_BASE) return '';
  try {
    const res = await fetch(`${API_BASE}/payment/checkout/config/`);
    if (!res.ok) return '';
    const data = await res.json();
    return (data?.publishable_key || '').trim();
  } catch (e) {
    return '';
  }
}

async function saveInvoiceProfile(clientSecret, businessName, taxId) {
  if (!API_BASE) return;
  const name = (businessName || '').trim();
  const vat = (taxId || '').trim();
  if (!name && !vat) return;
  const res = await fetch(`${API_BASE}/payment/checkout/invoice-profile/`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_secret: clientSecret,
      business_name: name,
      tax_id: vat,
    }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const data = await res.json();
      detail = data.error || data.detail || '';
    } catch (e) {
      /* ignore */
    }
    throw new Error(detail || 'Failed to save invoice details');
  }
}

function CheckoutForm({ clientSecret, initialCompany = '', initialTax = '', email = '' }) {
  const { t } = useTranslation();
  const checkoutState = useCheckout();
  const [companyName, setCompanyName] = useState(initialCompany);
  const [taxId, setTaxId] = useState(initialTax);
  const [coupon, setCoupon] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expressVisible, setExpressVisible] = useState(false);

  const checkout = checkoutState.type === 'success' ? checkoutState.checkout : null;

  const pay = useCallback(async (confirmArgs) => {
    if (!checkout) return;
    setFormError('');
    setBusy(true);
    try {
      const serverResult = await checkout.runServerUpdate(() =>
        saveInvoiceProfile(clientSecret, companyName, taxId),
      );
      if (serverResult?.type === 'error') {
        throw new Error(serverResult.error?.message || t('checkoutError'));
      }
      const result = await checkout.confirm(confirmArgs);
      if (result.type === 'error') {
        throw new Error(result.error?.message || t('checkoutError'));
      }
    } catch (err) {
      setFormError(err.message || t('checkoutError'));
      setBusy(false);
    }
  }, [checkout, clientSecret, companyName, taxId, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await pay();
  };

  const handleApplyCoupon = async (event) => {
    event.preventDefault();
    if (!checkout || !coupon.trim()) return;
    setFormError('');
    const result = await checkout.applyPromotionCode(coupon.trim());
    if (result.type === 'error') {
      setFormError(result.error?.message || t('checkoutCouponInvalid'));
    } else {
      setCoupon('');
    }
  };

  const handleRemoveCoupon = async () => {
    if (!checkout) return;
    setFormError('');
    await checkout.removePromotionCode();
  };

  if (checkoutState.type === 'loading') {
    return <p className="checkout-status">{t('checkoutProcessing')}</p>;
  }
  if (checkoutState.type === 'error') {
    return (
      <p className="checkout-error" role="alert">
        {checkoutState.error?.message || t('checkoutError')}
      </p>
    );
  }

  const total = checkout.total?.total?.amount || '';
  const discount = checkout.discountAmounts?.[0];
  const lineName = checkout.lineItems?.[0]?.name;

  return (
    <form className="checkout-form" onSubmit={handleSubmit}>
      {email ? (
        <p className="checkout-paying-as">{t('checkoutPayingAs', { email })}</p>
      ) : null}

      {lineName && (
        <div className="checkout-summary">
          <p className="checkout-plan">{lineName}</p>
          {discount && (
            <p className="checkout-discount">
              {t('checkoutDiscount')}: {discount.displayName}
              {discount.promotionCode ? ` (${discount.promotionCode})` : ''}
            </p>
          )}
          <p className="checkout-total">
            {t('checkoutTotal')}: <strong>{total}</strong>
          </p>
        </div>
      )}

      <ExpressCheckoutElement
        className={`checkout-express ${expressVisible ? 'is-visible' : ''}`}
        onReady={(event) => {
          const methods = event?.availablePaymentMethods;
          setExpressVisible(!!(methods && Object.values(methods).some(Boolean)));
        }}
        onConfirm={(event) => pay({ expressCheckoutConfirmEvent: event })}
      />
      {expressVisible && (
        <p className="checkout-or">{t('checkoutOrPayWithCard')}</p>
      )}

      <label className="checkout-label" htmlFor="checkout-company">
        {t('checkoutCompanyName')}
        <span className="checkout-optional"> {t('checkoutOptional')}</span>
      </label>
      <input
        id="checkout-company"
        className="checkout-input"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        placeholder={t('checkoutCompanyNamePlaceholder')}
        autoComplete="organization"
      />

      <label className="checkout-label" htmlFor="checkout-tax">
        {t('checkoutTaxNumber')}
        <span className="checkout-optional"> {t('checkoutOptional')}</span>
      </label>
      <input
        id="checkout-tax"
        className="checkout-input"
        value={taxId}
        onChange={(e) => setTaxId(e.target.value)}
        placeholder={t('checkoutTaxNumberPlaceholder')}
        autoCapitalize="characters"
        autoComplete="off"
      />
      <p className="checkout-hint">{t('checkoutTaxNumberHint')}</p>

      <label className="checkout-label" htmlFor="checkout-coupon">
        {t('checkoutCoupon')}
        <span className="checkout-optional"> {t('checkoutOptional')}</span>
      </label>
      <div className="checkout-coupon-row">
        <input
          id="checkout-coupon"
          className="checkout-input"
          value={coupon}
          onChange={(e) => setCoupon(e.target.value)}
          placeholder={t('checkoutCouponPlaceholder')}
          autoCapitalize="characters"
        />
        <button type="button" className="btn btn-secondary checkout-coupon-btn" onClick={handleApplyCoupon}>
          {t('checkoutApplyCoupon')}
        </button>
      </div>
      {discount && (
        <button type="button" className="checkout-remove-coupon" onClick={handleRemoveCoupon}>
          {t('checkoutRemoveCoupon')}
        </button>
      )}

      <div className="checkout-stripe">
        <PaymentElement
          options={{
            layout: 'tabs',
            fields: {
              billingDetails: {
                email: 'never',
                address: {
                  line1: 'never',
                  line2: 'never',
                  city: 'never',
                  state: 'never',
                  postalCode: 'never',
                },
              },
            },
          }}
        />
      </div>

      {formError ? (
        <p className="checkout-error" role="alert">{formError}</p>
      ) : null}

      <button type="submit" className="btn btn-primary checkout-pay" disabled={busy || !checkout.canConfirm}>
        {busy ? t('checkoutProcessing') : `${t('checkoutPay')} ${total}`.trim()}
      </button>
      <p className="checkout-secure">{t('checkoutSecure')}</p>
    </form>
  );
}

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embed') === '1';
  const [clientSecret, setClientSecret] = useState(readClientSecret);
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const [publishableKey, setPublishableKey] = useState(PUBLISHABLE_KEY);
  const [keyReady, setKeyReady] = useState(!!PUBLISHABLE_KEY);

  useEffect(() => {
    if (PUBLISHABLE_KEY) return undefined;
    let cancelled = false;
    loadPublishableKey().then((key) => {
      if (cancelled) return;
      if (key) setPublishableKey(key);
      setKeyReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const lng = searchParams.get('lng');
    if (lng && STRIPE_LOCALES.includes(lng.split('-')[0])) {
      i18n.changeLanguage(lng.split('-')[0]);
    }
  }, [i18n, searchParams]);

  useEffect(() => {
    const onHash = () => setClientSecret(readClientSecret());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!clientSecret || !API_BASE) {
      setProfileReady(true);
      return;
    }
    let cancelled = false;
    setProfileReady(false);
    fetch(`${API_BASE}/payment/checkout/invoice-profile/`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_secret: clientSecret }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setProfile(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProfileReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clientSecret]);

  const locale = stripeLocale(i18n.language);
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey, { locale }) : null),
    [publishableKey, locale],
  );
  const ready = keyReady && (!clientSecret || profileReady);
  const showForm = ready && publishableKey && clientSecret && stripePromise;

  return (
    <div className={`checkout-page${embedded ? ' is-embed' : ''}`}>
      {!embedded && (
        <header className="checkout-header">
          <Link to="/" className="checkout-brand" aria-label={t('appName')}>
            <img src="/images/logo.png" alt="" />
            <span><span className="titleGlow">Glow</span><span className="titleApp">App</span></span>
          </Link>
          <a className="checkout-back" href={APP_URL}>{t('checkoutBack')}</a>
        </header>
      )}
      <main className="checkout-main">
        {!embedded && (
          <>
            <h1>{t('checkoutTitle')}</h1>
            <p className="checkout-subtitle">{t('checkoutSubtitle')}</p>
          </>
        )}
        {keyReady && !publishableKey && (
          <p className="checkout-error" role="alert">{t('checkoutMissingKey')}</p>
        )}
        {publishableKey && !clientSecret && (
          <div className="checkout-login">
            <p>{t('checkoutLoginBody')}</p>
            <a className="btn btn-primary" href={APP_URL}>{t('checkoutLoginCta')}</a>
          </div>
        )}
        {!ready && publishableKey && (
          <p className="checkout-status">{t('checkoutProcessing')}</p>
        )}
        {showForm && (
          <CheckoutElementsProvider
            stripe={stripePromise}
            options={{
              clientSecret,
              elementsOptions: {
                fonts: [
                  {
                    cssSrc: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap',
                  },
                ],
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: embedded ? APP_PRIMARY : LANDING_PRIMARY,
                    borderRadius: embedded ? '10px' : '12px',
                    fontFamily: '"Plus Jakarta Sans", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  },
                },
              },
            }}
          >
            <CheckoutForm
              clientSecret={clientSecret}
              initialCompany={profile?.business_name || ''}
              initialTax={profile?.tax_id || ''}
              email={profile?.email || ''}
            />
          </CheckoutElementsProvider>
        )}
      </main>
    </div>
  );
};

export default CheckoutPage;
